<template>
  <!-- eslint-disable max-len -->
  <div v-if="hasAvailabilities" class="row row--flush !mb-0">
    <div class="col" :class="alwaysMobileVariant ? 's12' : 's12 m6'">
      <v-calendar
        id="availability-calendar"
        is-expanded
        trim-weeks
        :min-date="new Date()"
        nav-visibility="hidden"
        :theme-styles="themeStyles"
        :attributes="calendarAttributes"
        :formats="formats"
        :pane-width="220"
        @dayclick="triggerDateSelected"
      ></v-calendar>
    </div>
    <div class="col" :class="alwaysMobileVariant ? 's12 mt-24' : 's12 m6'">
      <div v-if="hasSelectedAvailabilities" class="c-pane-container--list">
        <ul>
          <CalendarCard v-for="availability in selectedAvailabilities" :key="availability.cursor" :availability="availability" />
        </ul>
      </div>
    </div>
  </div>
  <!-- eslint-enable max-len -->
</template>

<script lang="ts">
import { isSameDay, parseISO } from 'date-fns';
import { Calendar } from 'v-calendar';
import { ACCOUNT_AVAILABILITIES_QUERY } from '../../graphql/availability/queries';
import { drill } from '../../lib/objectTools';
import CalendarCard from './CalendarCard.vue';

export default {
  name: 'AvailabilityCalendar',
  components: {
    CalendarCard,
    VCalendar: Calendar,
  },
  props: {
    accountId: {
      type: String,
      required: true,
    },
    alwaysMobileVariant: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data(): any {
    return {
      account: null,
      selectedDate: null,
    };
  },
  apollo: {
    account: {
      query: ACCOUNT_AVAILABILITIES_QUERY,
      variables(): any {
        return {
          id: this.accountId,
          kind: 'SITTER',
          count: 20,
        };
      },
      result() {
        this.showMore();
      },
    },
  },
  computed: {
    hasAvailabilities(): boolean {
      return this.availabilities && this.availabilities.length > 0;
    },
    hasSelectedAvailabilities(): boolean {
      return this.selectedAvailabilities && this.selectedAvailabilities.length > 0;
    },
    availabilities(): Array<any> | null {
      return drill(this.account, ['availabilities', 'edges']);
    },
    formats(): object {
      return { title: 'MMM YYYY' };
    },
    themeStyles(): object {
      return {
        wrapper: { backgroundColor: '#ffffff' },
        header: {
          justifyContent: 'center',
          color: '#05070f',
          textTransform: 'uppercase',
        },
        weekdays: {
          paddingBottom: '.5rem',
          borderBottom: '1px solid #f0f1f1',
          color: '#05070f',
        },
        weeks: {
          color: '#05070f',
        },
        dayCellNotInMonth: {
          color: '#a5a7aa',
          opacity: 1,
        },
      };
    },
    highlightDate(): Date {
      if (this.selectedDate) {
        return this.selectedDate.date;
      }

      if (this.hasAvailabilities) {
        return parseISO(this.availabilities[0].node.start);
      }

      return new Date();
    },
    calendarAttributes(): Array<object> | undefined {
      const today = new Date();
      let attributes: Array<object> = [
        {
          highlight: {
            style: {
              backgroundColor: '#e0e0e0',
            },
          },
          content: {
            style: {
              color: '#05070f',
            },
          },
          dates: today,
        },
      ];
      if (!this.hasAvailabilities) return attributes;

      attributes = attributes.concat([
        {
          highlight: {
            style: {
              backgroundColor: '#99005C',
            },
          },
          content: {
            style: {
              color: '#ffffff',
            },
          },
          dates: this.highlightDate,
        },
      ]);

      const availabilityStartDates: Array<Date> = [];
      const bookedStartDates: Array<Date> = [];

      this.availabilities.forEach((avail) => {
        if (avail.node.kind === 'AVAILABLE') {
          availabilityStartDates.push(parseISO(avail.node.start));
        } else if (avail.node.kind === 'BOOKED') {
          bookedStartDates.push(parseISO(avail.node.start));
        }
      });

      return attributes.concat([
        {
          dot: {
            style: { backgroundColor: '#76787e', marginBottom: '-8px' },
          },
          dates: bookedStartDates,
        },
        {
          dot: {
            style: { backgroundColor: '#229150', marginBottom: '-8px' },
          },
          dates: availabilityStartDates,
        },
      ]);
    },
    selectedAvailabilities(): Array<any> {
      if (!this.availabilities) return [];

      return this.availabilities.filter((avail) => isSameDay(parseISO(avail.node.start), this.highlightDate));
    },
    hasMore(): boolean {
      return drill(this.account, ['availabilities', 'pageInfo', 'hasNextPage']);
    },
    lastAvailability(): string | null {
      const edges = drill(this.account, ['availabilities', 'edges']);
      if (edges && edges.length !== 0) {
        return edges[edges.length - 1].cursor;
      }

      return null;
    },
  },
  watch: {
    hasAvailabilities(newVal) {
      this.$emit('updateAvailabilities', newVal);
    },
  },
  methods: {
    triggerDateSelected(e) {
      this.selectedDate = e;
    },
    mergeResults(previousResult, { fetchMoreResult }): any {
      const e1 = drill(previousResult, ['account', 'availabilities', 'edges']);
      const e2 = drill(fetchMoreResult, ['account', 'availabilities', 'edges']);
      if (e1 && e2) {
        const combined = JSON.parse(JSON.stringify(fetchMoreResult)); // clone
        combined.account.availabilities.edges = e1.concat(e2);
        return combined;
      }

      return previousResult;
    },
    showMore() {
      if (this.hasMore) {
        this.$apollo.queries.account.fetchMore({
          variables: { after: this.lastAvailability },
          updateQuery: this.mergeResults,
        });
      }
    },
  },
};
</script>

<style lang="scss">
#availability-calendar {
  font-family: 'Graphik Web', Helvetica, Arial, sans-serif;
  &.vc-container {
    border: 0;
  }

  .vc-weekday,
  .vc-title {
    color: #05070f;
  }

  .vc-title {
    font-size: 16px;
  }

  .vc-header {
    margin-bottom: 1rem;
  }

  .vc-day {
    margin-bottom: 8px;
  }

  .vc-day-content.is-disabled {
    color: #6e6f74;
  }
}
</style>
